import { Title, Text, Stack, ActionIcon } from '@mantine/core';
import { FileDrop } from '../../molecules';
import { useFileSelect } from './use-file-select';
import {
  Container,
  ContentWrapper,
  InputWrapper,
  RecentFilesWrapper,
  RecentFileItem,
} from './styles';
import { t } from 'i18next';
import { RecentFile } from '@/types';
import { useTranslation } from 'react-i18next';
import { useFetchUserProfile } from '@/api';
import { useEffect } from 'react';
import { useStore } from '@/store';
import { FiTrash2, FiDownload } from 'react-icons/fi';

export interface FileSelectProps {
  children: (progress: () => void, disabled?: boolean) => JSX.Element;
}

const formatDate = (date: Date) => {
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

interface RecentFileItemProps {
  file: RecentFile;
  onSelect: (fileId: string) => void;
  onDownload: (fileId: string) => void;
  onDelete: (fileId: string) => void;
}

interface RecentFilesProps {
  files: RecentFile[];
  onSelect: (fileId: string) => void;
  onDownload: (fileId: string) => void;
  onDelete: (fileId: string) => void;
}

const RecentFileItemComponent = ({
  file,
  onSelect,
  onDownload,
  onDelete,
}: RecentFileItemProps) => {
  const { t } = useTranslation();
  const {
    data: userProfile,
    isLoading,
    error,
  } = useFetchUserProfile(file.userId);
  const formattedDate = formatDate(file.usedAt);

  const handleDownloadClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDownload(file.fileId);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(file.fileId);
  };

  return (
    <RecentFileItem onClick={() => onSelect(file.fileId)}>
      <Text size="sm" truncate style={{ flex: 1, minWidth: 0 }}>
        {file.fileName}
      </Text>
      <Text
        size="xs"
        color="dimmed"
        style={{ marginLeft: '1rem', whiteSpace: 'nowrap' }}
      >
        {isLoading
          ? 'Loading...'
          : error
            ? ''
            : userProfile?.name
              ? `${userProfile.name} • ${formattedDate}`
              : formattedDate}
      </Text>
      <ActionIcon
        variant="subtle"
        size="sm"
        onClick={handleDownloadClick}
        aria-label={t('button.downloadRecentFile')}
      >
        <FiDownload size={14} />
      </ActionIcon>
      <ActionIcon
        variant="subtle"
        color="red"
        size="sm"
        onClick={handleDeleteClick}
        aria-label={t('button.removeFromRecentFiles')}
      >
        <FiTrash2 size={14} />
      </ActionIcon>
    </RecentFileItem>
  );
};

const RecentFiles = ({
  files,
  onSelect,
  onDownload,
  onDelete,
}: RecentFilesProps) => {
  const { t } = useTranslation();

  if (files.length === 0) {
    return null;
  }

  return (
    <RecentFilesWrapper>
      <Title order={4} fw={500} mb="md">
        {t('title.recentFiles')}
      </Title>
      <Stack spacing="xs">
        {files.map((file) => (
          <RecentFileItemComponent
            key={file.fileId}
            file={file}
            onSelect={onSelect}
            onDownload={onDownload}
            onDelete={onDelete}
          />
        ))}
      </Stack>
    </RecentFilesWrapper>
  );
};

export const FileSelect = ({ children }: FileSelectProps) => {
  /* Global state */
  const clearFile = useStore(({ config: c }) => c.clearFile);
  /* Hooks */
  const {
    selectedFiles,
    handleFileSelect,
    handleRemoveFile,
    progressSetup,
    disableNext,
    getAcceptedFileExtensions,
    getAcceptedFileExtensionsDisplay,
    recentFiles,
    handleRecentFileSelect,
    handleRecentFileDownload,
    handleRecentFileDelete,
  } = useFileSelect();

  // Ensure a fresh file selection each time this screen is shown
  useEffect(() => {
    clearFile();
  }, []);

  const description = t('description.fileDrop', {
    extensions: getAcceptedFileExtensionsDisplay().join(', '),
  });

  return (
    <>
      <Container>
        <ContentWrapper>
          <Title order={3} fw={500}>
            {t('title.selectFile')}
          </Title>

          <InputWrapper>
            <FileDrop
              fileExtenstions={getAcceptedFileExtensions()}
              description={description}
              load={handleFileSelect}
            />
            {selectedFiles.length > 0 ? (
              <Stack spacing="xs" mt="sm">
                <Text size="xs" color="dimmed" tt="uppercase" fw={600} mt={4}>
                  {t('title.selectedFiles')} ({selectedFiles.length})
                </Text>
                {selectedFiles.map((f, i) => (
                  <RecentFileItem key={i} style={{ cursor: 'default' }}>
                    <Text size="sm" truncate style={{ flex: 1, minWidth: 0 }}>
                      {f.name}
                    </Text>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      size="sm"
                      onClick={() => handleRemoveFile(i)}
                      aria-label={t('button.removeFile')}
                    >
                      <FiTrash2 size={14} />
                    </ActionIcon>
                  </RecentFileItem>
                ))}
              </Stack>
            ) : (
              <RecentFiles
                files={recentFiles}
                onSelect={handleRecentFileSelect}
                onDownload={handleRecentFileDownload}
                onDelete={handleRecentFileDelete}
              />
            )}
          </InputWrapper>
        </ContentWrapper>
      </Container>

      {children(progressSetup, disableNext)}
    </>
  );
};
